import { useEffect } from 'react';

import { useImportWizardStore } from '../store/useImportWizardStore';
import { autoMatchColumns } from '../utils/columnMatcher';

/**
 * Target field names for deal fields and address fields.
 * These are the system field names that auto-matching will attempt
 * to match against file column headers.
 */
const DEAL_FIELDS = ['extID', 'name', 'description', 'notes'];
const ADDRESS_FIELDS = [
  'latitude',
  'longitude',
  'street',
  'city',
  'region',
  'country',
  'postalCode',
];

/**
 * Hook that auto-matches file column headers to system field names
 * using case-insensitive comparison. Pre-populates the mapping config
 * when the mapping step loads, but only if the mapping config is empty
 * (does not override user selections).
 *
 * Validates: Requirements 9.1, 9.2
 */
export function useSmartDefaults(): void {
  const headers = useImportWizardStore((state) => state.headers);
  const mappingConfig = useImportWizardStore((state) => state.mappingConfig);
  const updateMappingConfig = useImportWizardStore(
    (state) => state.updateMappingConfig
  );

  useEffect(() => {
    // Only apply defaults if headers are available and mappingConfig has no columnMappings yet
    if (headers.length === 0) return;
    if (mappingConfig.columnMappings) return;

    const allTargetFields = [...DEAL_FIELDS, ...ADDRESS_FIELDS];
    const matches = autoMatchColumns(headers, allTargetFields);

    // If no matches found, nothing to pre-populate
    if (Object.keys(matches).length === 0) return;

    // Build columnMappings from matched fields
    const dealFields: Record<string, string> = {};
    for (const field of DEAL_FIELDS) {
      if (matches[field]) {
        dealFields[field] = matches[field];
      }
    }

    const addressFields: Record<string, string> = {};
    for (const field of ADDRESS_FIELDS) {
      if (matches[field]) {
        addressFields[field] = matches[field];
      }
    }

    const columnMappings: Record<string, unknown> = {};

    if (Object.keys(dealFields).length > 0) {
      columnMappings.dealFields = dealFields;
    }

    if (Object.keys(addressFields).length > 0) {
      columnMappings.addressFields = addressFields;
    }

    if (Object.keys(columnMappings).length > 0) {
      updateMappingConfig({ columnMappings } as any);
    }
  }, [headers, mappingConfig.columnMappings, updateMappingConfig]);
}
